import * as React from "react";
import clsx from "clsx";
import { ValidationMessage, useValidationStore } from "@/stores/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  UseFormReturn,
  SubmitHandler,
  UseFormProps,
  FieldValues,
  SubmitErrorHandler,
  Path,
  DefaultValues,
} from "react-hook-form";
import { ZodType, ZodTypeDef } from "zod";
import isEqual from "lodash/isEqual";

type FormProps<TFormValues extends FieldValues, Schema> = {
  className?: string;
  onSubmit: SubmitHandler<TFormValues>;
  children: (methods: UseFormReturn<TFormValues>) => React.ReactNode;
  options?: UseFormProps<TFormValues>;
  id?: string;
  schema?: Schema;
  debug?: boolean;
};

export const Form = <
  TFormValues extends Record<string, unknown> = Record<string, unknown>,
  Schema extends ZodType<unknown, ZodTypeDef, unknown> = ZodType<
    unknown,
    ZodTypeDef,
    unknown
  >,
>({
  onSubmit,
  children,
  className,
  options,
  id,
  schema,
  debug = false,
}: FormProps<TFormValues, Schema>) => {
  const submitProcessing = React.useRef(false);
  const { validations, clearValidations } = useValidationStore();

  const methods = useForm<TFormValues>({
    ...options,
    resolver: schema && zodResolver(schema),
  });

  const prevDefaultValues = React.useRef<
    DefaultValues<TFormValues> | undefined
  >(undefined);

  React.useEffect(() => {
    validations.forEach((validate: ValidationMessage) => {
      const key = validate.key as Path<TFormValues>;
      methods.setError(key, validate);
    });
    submitProcessing.current = false;
  }, [validations, methods]);

  React.useEffect(() => {
    if (options?.defaultValues) {
      if (!isEqual(options.defaultValues, prevDefaultValues.current)) {
        const defaultValues =
          options.defaultValues as DefaultValues<TFormValues>;
        methods.reset(defaultValues);
        prevDefaultValues.current = defaultValues;
      }
    }
  }, [options, methods]);

  const onError: SubmitErrorHandler<TFormValues> = (e) => {
    clearValidations();
    if (debug) console.log(e);
  };

  const onSubmitOverwrite: SubmitHandler<TFormValues> = (e) => {
    clearValidations();
    if (submitProcessing.current) return;
    submitProcessing.current = true;
    onSubmit(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    // Enterキーが押された場合の処理
    if (e.key === 'Enter') {
      const target = e.target;

      // input, select, textareaの場合
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLSelectElement ||
        target instanceof HTMLTextAreaElement
      ) {
        // type="submit"またはtype="button"のボタン以外ではEnterキーを無効化
        if (target.type !== 'submit' && target.type !== 'button') {
          e.preventDefault();
        }
      }
    }
  };

  return (
    <form
      className={clsx("space-y-6", className)}
      onSubmit={methods.handleSubmit(onSubmitOverwrite, onError)}
      onKeyDown={handleKeyDown}
      id={id}
      noValidate
    >
      {children(methods)}
    </form>
  );
};
